<template>
  <card-dialog
    :value="value"
    @input="$emit('input', $event)"
    :title="isEditing ? $t('title.edit') : $t('title.create')"
    :width="848"
    content-class="capture-record-dialog"
  >
    <v-form v-model="valid" ref="form">
      <div class="editor-body">
        <div class="editor-form">
          <form-field :label="$t('field.date')" required block>
            <date-picker-dropdown
              type="date"
              :value="form.date"
              @input="form.date = $event"
              :max="today"
            >
              <template #activator="{ on, attrs }">
                <text-field
                  :value="form.date"
                  :rules="[required($t('field.date'))]"
                  readonly
                  v-on="on"
                  v-bind="attrs"
                  hide-details="auto"
                  data-testid="capture_date"
                />
              </template>
            </date-picker-dropdown>
          </form-field>

          <form-field :label="$t('field.count')" required block>
            <text-field
              v-model.number="form.count"
              type="number"
              :rules="countRules"
              hide-details="auto"
              data-testid="capture_count"
            />
          </form-field>

          <form-field :label="$t('field.species')" required block>
            <v-select
              v-model="form.species"
              :items="speciesOptions"
              :rules="[required($t('field.species'))]"
              dense
              outlined
              hide-details="auto"
              data-testid="capture_species"
            />
          </form-field>

          <div class="editor-divider" role="separator" />

          <div class="editor-optional-row">
            <form-field :label="$t('field.trap_type')" block>
              <v-select
                v-model="form.trap_type"
                :items="trapTypeOptions"
                dense
                outlined
                hide-details
                clearable
                data-testid="capture_trap_type"
              />
            </form-field>

            <form-field :label="$t('field.growth_stage')" block>
              <v-select
                v-model="form.growth_stage"
                :items="growthStageOptions"
                dense
                outlined
                hide-details
                clearable
                data-testid="capture_growth_stage"
              />
            </form-field>

            <form-field :label="$t('field.sex')" block>
              <v-select
                v-model="form.sex"
                :items="sexOptions"
                dense
                outlined
                hide-details
                clearable
                data-testid="capture_sex"
              />
            </form-field>
          </div>

          <form-field :label="$t('field.memo')" block>
            <v-textarea
              v-model="form.memo"
              no-resize
              dense
              outlined
              hide-details
              class="memo-area"
              data-testid="capture_memo"
            />
          </form-field>
        </div>

        <aside class="mp-sidebar" :aria-label="$mpt($t('field.sensors'))">
          <div class="mp-sidebar__inner">
            <span class="mp-sidebar__label">{{ $mpt($t('field.sensors')) }}</span>
            <monitoring-point-selector
              v-model="form.sensor_ids"
              class="mp-selector"
              :sensors="sensors"
              :orphaned-sensor-ids="orphanedSensorIds"
              :visible="value"
            />
          </div>
        </aside>
      </div>
    </v-form>

    <template #actions>
      <dialog-action @click="close" data-testid="cancel_button">
        {{ $t('button.cancel') }}
      </dialog-action>
      <dialog-action
        @click="save"
        :disabled="saving || !valid"
        data-testid="save_button"
      >
        {{ saving ? $t('button.saving') : (isEditing ? $t('button.save') : $t('button.create')) }}
      </dialog-action>
    </template>
  </card-dialog>
</template>

<i18n lang="yaml">
ja:
  title:
    create: 捕獲記録を追加
    edit: 捕獲記録を編集
  field:
    date: 確認日
    count: 捕獲数
    species: 種類
    sensors: 最寄りのセンサー
    trap_type: トラップ種類
    growth_stage: 発育段階
    sex: 性別
    memo: メモ
  species:
    brown_rat: クマネズミ
    black_rat: ドブネズミ
    house_mouse: ハツカネズミ
    other: その他
    unknown: 不明
  trap_type:
    glue: 粘着
    cage: かご
    snap: スナップ
    other: その他
  growth_stage:
    juvenile: 幼獣
    adult: 成獣
  sex:
    male: オス
    female: メス
    unknown: 不明
  button:
    cancel: キャンセル
    save: 保存
    create: 追加
    saving: 保存中...
  msg:
    save_error: 捕獲記録の保存に失敗しました
  validation:
    min_one: 1以上の値を入力してください
en:
  title:
    create: Add Capture Record
    edit: Edit Capture Record
  field:
    date: Date
    count: Count
    species: Species
    sensors: Nearest Sensors
    trap_type: Trap Type
    growth_stage: Growth Stage
    sex: Sex
    memo: Memo
  species:
    brown_rat: Brown Rat
    black_rat: Black Rat
    house_mouse: House Mouse
    other: Other
    unknown: Unknown
  trap_type:
    glue: Glue
    cage: Cage
    snap: Snap
    other: Other
  growth_stage:
    juvenile: Juvenile
    adult: Adult
  sex:
    male: Male
    female: Female
    unknown: Unknown
  button:
    cancel: Cancel
    save: Save
    create: Add
    saving: "Saving..."
  msg:
    save_error: Failed to save capture record
  validation:
    min_one: Enter a value of 1 or more
</i18n>

<script>
import CardDialog from '@/components/atoms/CardDialog';
import DialogAction from '@/components/atoms/DialogAction';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';
import DatePickerDropdown from '@/components/atoms/DatePickerDropdown';
import Notifications from '@/mixins/notifications';
import Validations from '@/components/Form/mixins/form-validations';
import { format } from 'date-fns';
import MonitoringPointSelector from '@/components/molecules/MonitoringPointSelector';

export default {
  name: 'capture-record-editor',
  components: {
    CardDialog, DialogAction, FormField, TextField, DatePickerDropdown, MonitoringPointSelector,
  },
  mixins: [Notifications, Validations],
  inject: ['api'],
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
    sensors: { type: Array, default: () => [] },
    placeId: { type: String, required: true },
    defaultSensorIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      valid: false,
      saving: false,
      form: this.buildInitialForm(),
    };
  },
  computed: {
    isEditing() {
      return this.record != null;
    },
    today() {
      return format(new Date(), 'yyyy-MM-dd');
    },
    countRules() {
      return [
        this.required(this.$t('field.count')),
        v => v >= 1 || this.$t('validation.min_one'),
      ];
    },
    // 選択肢の value は BE の定数と一致させること
    // BE: src/pestvision/capture_record/capture_record.py
    //   SPECIES_OPTIONS, TRAP_TYPE_OPTIONS, GROWTH_STAGE_OPTIONS, SEX_OPTIONS
    speciesOptions() {
      return [
        { text: this.$t('species.brown_rat'), value: 'brown_rat' },
        { text: this.$t('species.black_rat'), value: 'black_rat' },
        { text: this.$t('species.house_mouse'), value: 'house_mouse' },
        { text: this.$t('species.other'), value: 'other' },
        { text: this.$t('species.unknown'), value: 'unknown' },
      ];
    },
    trapTypeOptions() {
      return [
        { text: this.$t('trap_type.glue'), value: 'glue' },
        { text: this.$t('trap_type.cage'), value: 'cage' },
        { text: this.$t('trap_type.snap'), value: 'snap' },
        { text: this.$t('trap_type.other'), value: 'other' },
      ];
    },
    growthStageOptions() {
      return [
        { text: this.$t('growth_stage.juvenile'), value: 'juvenile' },
        { text: this.$t('growth_stage.adult'), value: 'adult' },
      ];
    },
    orphanedSensorIds() {
      if (this.record == null) return [];
      const sensorIdSet = new Set(this.sensors.map(s => s.id));
      return (this.record.sensor_ids || []).filter(id => !sensorIdSet.has(id));
    },
    sexOptions() {
      return [
        { text: this.$t('sex.male'), value: 'male' },
        { text: this.$t('sex.female'), value: 'female' },
        { text: this.$t('sex.unknown'), value: 'unknown' },
      ];
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetForm();
      }
    },
    record() {
      this.resetForm();
    },
  },
  methods: {
    buildInitialForm() {
      return {
        date: '',
        count: null,
        species: null,
        sensor_ids: [...this.defaultSensorIds],
        trap_type: null,
        growth_stage: null,
        sex: null,
        memo: '',
      };
    },
    resetForm() {
      if (this.record != null) {
        this.form = {
          date: this.record.date,
          count: this.record.count,
          species: this.record.species,
          sensor_ids: [...(this.record.sensor_ids || [])],
          trap_type: this.record.trap_type,
          growth_stage: this.record.growth_stage,
          sex: this.record.sex,
          memo: this.record.memo || '',
        };
      } else {
        this.form = this.buildInitialForm();
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    close() {
      this.$emit('input', false);
    },
    async save() {
      try {
        this.saving = true;
        const body = this.buildRequestBody();
        if (this.isEditing) {
          const updated = await this.api.updateRTypeCaptureRecord(this.record.id, body);
          this.$emit('saved', updated);
        } else {
          const created = await this.api.createRTypeCaptureRecord(this.placeId, body);
          this.$emit('saved', created);
        }
        this.close();
      } catch {
        this.notifyError('msg.save_error');
      } finally {
        this.saving = false;
      }
    },
    buildRequestBody() {
      const body = {
        date: this.form.date,
        count: this.form.count,
        species: this.form.species,
      };
      body.sensor_ids = this.form.sensor_ids;
      if (this.form.trap_type != null) { body.trap_type = this.form.trap_type; }
      if (this.form.growth_stage != null) { body.growth_stage = this.form.growth_stage; }
      if (this.form.sex != null) { body.sex = this.form.sex; }
      if (this.form.memo != null) { body.memo = this.form.memo; }
      return body;
    },
  },
};
</script>

<style>
.capture-record-dialog .v-card__title.headline {
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
}
</style>

<style scoped>
::v-deep .v-input {
  font-size: 14px;
}

.editor-body {
  display: flex;
  align-items: stretch;
}

.editor-form {
  flex: 1;
  min-width: 0;
  padding-right: 24px;
}

/* 必須項目と任意項目の切れ目。ここから下は埋めなくても保存できる */
.editor-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 4px 0 12px;
}

.editor-optional-row {
  display: flex;
  gap: 12px;
}

.editor-optional-row > * {
  flex: 1;
  min-width: 0;
}

/* 面の色を変えて「記入する欄」ではなく「一覧から選ぶ場所」だと見た目で示す。
   高さは左ペインに従わせる。中身を素直にフローへ置くと右ペインの高さが
   本文の高さを押し上げてしまうため、中身は絶対配置に逃がして
   サイドバー自身の内在高さを 0 にしておく（align-items: stretch で左に揃う）。
   面の色は子（見出しの固定表示）にも必要なため CSS 変数で供給する */
.mp-sidebar {
  --mp-surface: #f5f5f5;

  width: 400px;
  flex-shrink: 0;
  position: relative;
  background: var(--mp-surface);
  border-radius: 4px;
}

.mp-sidebar__inner {
  position: absolute;
  inset: 0;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mp-sidebar__label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}

/* 子コンポーネントのルート要素。Vue 2 の scoped CSS は子のルートにも
   親のスコープ属性を付けるため、親から高さの伸び方を指定できる */
.mp-selector {
  flex: 1;
  min-height: 0;
}

.memo-area ::v-deep .v-input__slot {
  height: 200px;
}

.memo-area ::v-deep textarea {
  overflow-y: auto;
}

/* 左右に並べる幅が無くなったら縦積みにする */
@media (max-width: 959px) {
  .editor-body {
    display: block;
  }

  .editor-form {
    padding-right: 0;
  }

  .mp-sidebar {
    width: auto;
    position: static;
    margin-top: 16px;
  }

  .mp-sidebar__inner {
    position: static;
    height: 320px;
  }
}
</style>
